import { useMemo } from 'react';

import { useGetCategories } from '@/actions/category';
import type { SubDrawer, SubDrawerItem } from './navbarData';

// ----------------------------------------------------------------------

/**
 * Builds the "formations" SubDrawer dynamically from the API categories.
 *
 * Each root category becomes a SubDrawerItem (left panel).
 * Its subcategories become subItems (right panel) + a dedicated sub-SubDrawer
 * for mobile navigation.
 *
 * Falls back to `null` while loading so callers can show a skeleton or the
 * static drawer as a fallback.
 */
export function useFormationsDrawer(): {
  drawer: SubDrawer | null;
  subDrawers: Record<string, SubDrawer>;
  isLoading: boolean;
} {
  const { categories, categoriesLoading } = useGetCategories();

  return useMemo(() => {
    if (!categories.length) {
      return { drawer: null, subDrawers: {}, isLoading: categoriesLoading };
    }

    // Extra sub-drawers keyed by "cat-{slug}" — used by the mobile navigator
    const extraSubDrawers: Record<string, SubDrawer> = {};

    const items: SubDrawerItem[] = categories.map((cat) => {
      const subDrawerId = `cat-${cat.slug}`;
      const subItems = (cat.subcategories ?? []).map((sub) => ({
        label: sub.name,
        href: `/formations/${sub.slug}`,   // flat URL — each subcategory has its own page
      }));

      // Mobile sub-drawer for each root category
      extraSubDrawers[subDrawerId] = {
        id: subDrawerId,
        title: cat.name,
        bgColor: '#C7F8FF',
        items: subItems.map((s) => ({ label: s.label, href: s.href, subItems: [] })),
        ctas: [
          {
            id: `${subDrawerId}-all`,
            label: `Voir toutes les formations ${cat.name}`,
            href: `/formations/${cat.slug}`,
          },
          {
            id: `${subDrawerId}-contact`,
            label: 'Contacter un conseiller',
            href: '/contact',
          },
        ],
      };

      return {
        label: cat.name,
        href: `/formations/${cat.slug}`,
        subDrawerId,
        subItems,
      };
    });

    const drawer: SubDrawer = {
      id: 'formations',
      title: 'Formations',
      items,
      ctas: [
        {
          id: 'all-trainings',
          label: 'Découvrir toutes nos formations',
          href: '/formations',
          className: 'hero__btn-primary',
          variant: 'primary',
        },
        {
          id: 'financement',
          label: 'Être accompagné dans mon financement',
          href: '/contact',
          className: 'btn--ghost',
          variant: 'ghost',
        },
      ],
    };

    return { drawer, subDrawers: extraSubDrawers, isLoading: false };
  }, [categories, categoriesLoading]);
}
